import { API_MODE, delay } from "./helpers";
import { request } from "./http";
import * as mocks from "@/lib/mocks/notifications";
import { disabledWords } from "./schedule-reasons";
import {
  paths, WIRE_ISSUE_STATES, WIRE_SCAN_STATES, type WireNotification, type WireNotificationPage, type WireSeverity,
} from "./wire";
import { issueStateLabel } from "@/components/shell/issue-state";
import { scanStatusLabel } from "@/components/shell/severity";
import { classLabel } from "@/lib/findings/class-label";

/**
 * In-app notifications — FR-68's in-app channel, CMP-046's tenant arm
 * (ADR-075, `notifyapi`).
 *
 * SOURCE-MIRRORED from `notifyapi.notificationResponse` and `notificationPage`.
 * A notification is the caller's own, or tenant-wide (raised by a `tenant`
 * rule, seen by every member); read state is PER CALLER, so marking a
 * tenant-wide one read reads it for nobody else. `unread` is the badge.
 */

export type NotificationEvent = WireNotification["event"];

export interface AppNotification {
  id: string;
  /** Absent for a platform notification, which no rule raised (ADR-101). */
  ruleId?: string;
  /** Who raised it: an alert rule, or the platform itself. */
  source: "rule" | "platform" | (string & {});
  /** On a platform notification, who may read it (`org_admins`). */
  audience?: string;
  event: NotificationEvent;
  /** Absent for a scan event, which carries none. */
  severity?: WireSeverity;
  /**
   * The public event's `data`, as the webhook event of the same name documents
   * it. Typed fields only — but `target_url` and the like came from a target:
   * render as TEXT, never as markup.
   */
  data: Record<string, unknown>;
  tenantWide: boolean;
  read: boolean;
  createdAt: string;
}

export interface NotificationPage {
  items: AppNotification[];
  unread: number;
  nextCursor?: string;
}

export function notificationFromWire(w: WireNotification): AppNotification {
  return {
    id: w.notification_id,
    ...(w.rule_id ? { ruleId: w.rule_id } : {}),
    source: w.source,
    ...(w.audience ? { audience: w.audience } : {}),
    event: w.event,
    ...(w.severity ? { severity: w.severity } : {}),
    data: w.data ?? {},
    tenantWide: w.tenant_wide,
    read: w.read,
    createdAt: w.created_at,
  };
}

export async function listNotifications(cursor?: string): Promise<NotificationPage> {
  const wire: WireNotificationPage = API_MODE === "http"
    ? await request<WireNotificationPage>(paths.notifications(), { query: cursor ? { cursor } : undefined })
    : await (async () => { await delay(120); return mocks.page(); })();
  return {
    items: wire.items.map(notificationFromWire),
    unread: wire.unread,
    ...(wire.next_cursor ? { nextCursor: wire.next_cursor } : {}),
  };
}

/** Mark one notification read, for the caller. Idempotent. */
export async function markNotificationRead(id: string): Promise<void> {
  if (API_MODE === "http") {
    await request<void>(paths.notificationRead(id), { method: "POST" });
    return;
  }
  await delay(80);
  mocks.markRead(id);
}

/**
 * A one-line, TEXT-ONLY summary of a notification for the list and the badge's
 * menu. Built from the typed fields the event documents; nothing is rendered as
 * markup, and a field this build does not recognise is simply not used.
 */
/** 3.114: a severity as a word in a sentence; own keys only (the value is untrusted). */
const SEVERITY_WORD: Record<string, string> = Object.assign(Object.create(null) as Record<string, string>, {
  critical: "critical", high: "high", medium: "medium", low: "low", info: "informational",
});

export function summarise(n: AppNotification): string {
  const d = n.data;
  const str = (k: string) => (typeof d[k] === "string" ? (d[k] as string) : undefined);
  // 3.114: each value in the console's own words, the same word its badge
  // shows; a value this build does not know is quoted, never mapped to a
  // neighbour (invariant 9). The raw sentences ("Issue moved from in_fix to
  // validating") read as code.
  const issueState = (s: string) =>
    (WIRE_ISSUE_STATES as readonly string[]).includes(s) ? issueStateLabel(s as (typeof WIRE_ISSUE_STATES)[number]) : `"${s}"`;
  switch (n.event) {
    case "scan.finished": {
      const state = str("state");
      if (!state) return "Scan ended";
      return (WIRE_SCAN_STATES as readonly string[]).includes(state)
        ? `Scan ended: ${scanStatusLabel(state as (typeof WIRE_SCAN_STATES)[number])}`
        : `Scan ended: "${state}"`;
    }
    case "finding.stored": {
      const cls = str("class");
      const what = cls ? classLabel(cls) : "unclassified";
      const sev = n.severity && SEVERITY_WORD[n.severity];
      return sev ? `New ${sev} finding: ${what}` : `New finding: ${what}`;
    }
    case "issue.state_changed": {
      const from = str("from");
      const to = str("to");
      if (!from) return to ? `Issue opened: ${issueState(to)}` : "Issue opened";
      return `Issue moved from ${issueState(from)} to ${to ? issueState(to) : "a new state"}`;
    }
    // ADR-101: the platform switched a schedule off; data is exactly
    // {schedule_id, disabled_reason, disabled_at}.
    case "schedule.disabled": {
      const reason = str("disabled_reason");
      return reason ? `A schedule was switched off: ${disabledWords(reason)}` : "A schedule was switched off";
    }
    default:
      return "Notification";
  }
}
